import { ErrorBoundary } from "react-error-boundary";
import cx from "classnames";
import { useTabsState } from "src/components/general/Tabs/Tabs.hooks";
import ToolboxButtons from "./components/ToolboxButtons/ToolboxButtons";
import ToolboxContentError from "./components/ToolboxContentError/ToolboxContentError";
import ToolboxSection from "./components/ToolboxSection/ToolboxSection";
import { BORDER_CLASSES } from "./Toolbox.constants";
import { useToolbox } from "./Toolbox.hooks";
import type { ToolboxProps } from "./Toolbox.types";

const Toolbox = ({
  ref,
  apiRef,
  children,
  className,
  initialValue,
  searchParam,
  cleanupSearchParamsBySection,
  slotProps,
  ...props
}: ToolboxProps) => {
  const [selected, setSelected] = useTabsState({
    searchParam,
    value: initialValue,
    cleanupSearchParamsByTab: cleanupSearchParamsBySection ?? {},
    searchParamOpts: { replace: true },
  });

  const { validChildren } = useToolbox({
    initialValue,
    selected: selected ?? undefined, // casting since "null" is not a valid value for the "selected" prop
    children,
    cleanupSearchParamsBySection,
  });

  const selectSection = (value: string) => {
    if (apiRef?.current) {
      apiRef.current.selectedSection = value;
    }
    setSelected(value);
  };

  const lastSelected = apiRef?.current?.selectedSection;
  const sectionsNames = validChildren.map((child) => child.props.value);
  const shouldSetSelectedToLastSelected =
    lastSelected && !selected && sectionsNames.includes(lastSelected);
  const deFactoSelected = shouldSetSelectedToLastSelected ? lastSelected : selected;

  const selectedContent = validChildren.find(
    (child) => child.props.value === deFactoSelected && child.props.children
  );

  return (
    <aside
      ref={ref}
      className={cx("h-full flex flex-row-reverse relative", className)}
      data-test-id="toolbox"
      {...props}
    >
      <section
        className={cx("p-1 bg-background-paper relative z-10", BORDER_CLASSES)}
      >
        <ToolboxButtons
          buttons={validChildren}
          selected={deFactoSelected}
          onChange={selectSection}
        />
      </section>

      {selectedContent && (
        // this extra wrapper is required for absolute-positioned sub-drawers (like GenericToolboxInsight),
        // so the sub-drawer will render OVER the current drawer's (potentioannly scrollable) content
        <div className="flex-1 overflow overflow-x-clip relative">
          <section
            data-test-id="toolbox-content"
            className={cx(
              "h-full w-full overflow-hidden overflow-y-auto bg-background-paper p-page pr-3 scroll-tracker @container/toolbox-content",
              BORDER_CLASSES,
              slotProps?.section?.className
            )}
            style={{
              paddingRight: "calc(var(--scroll) * 1rem + 0.75rem)",
              scrollbarGutter: "stable",
              ...slotProps?.section?.style,
            }}
          >
            <div className="min-h-full h-1">
              <ErrorBoundary
                fallback={<ToolboxContentError />}
                resetKeys={[deFactoSelected]}
              >
                {selectedContent}
              </ErrorBoundary>
            </div>
          </section>
        </div>
      )}
    </aside>
  );
};

Toolbox.Section = ToolboxSection;

export default Toolbox;
